package org.teddapp.project.ui.notes.ink

import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonArray
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.contentOrNull
import kotlinx.serialization.json.jsonPrimitive
import kotlinx.serialization.json.put
import kotlin.io.encoding.Base64
import kotlin.io.encoding.ExperimentalEncodingApi
import kotlin.math.roundToInt

/**
 * Un point d'un trait, en unités de page : une page fait [InkDrawing.WIDTH] de large,
 * quel que soit l'écran. [p] est la pression, de 0 à 1.
 */
internal class InkPoint(val x: Float, val y: Float, val p: Float)

internal enum class InkTool { Pen, Highlighter }

/**
 * Un trait : un outil, une couleur, une épaisseur, des points. Une classe et non une
 * data class : deux traits identiques restent deux traits, et l'égalité par identité
 * permet de garder le dessin calculé de chacun sans le recalculer à chaque image.
 *
 * [color] vaut [INK] pour l'encre du thème : noire sur le papier clair, claire sur le
 * sombre. Toute autre valeur est une couleur ARGB fixe.
 */
internal class InkStroke(
    val tool: InkTool,
    val color: Long,
    val width: Float,
    val points: List<InkPoint>,
) {
    /** Le rectangle qui contient le trait, épaisseur comprise : pour la gomme. */
    val bounds: FloatArray by lazy {
        var l = Float.MAX_VALUE; var t = Float.MAX_VALUE; var r = -Float.MAX_VALUE; var b = -Float.MAX_VALUE
        for (pt in points) { l = minOf(l, pt.x); t = minOf(t, pt.y); r = maxOf(r, pt.x); b = maxOf(b, pt.y) }
        val m = width
        floatArrayOf(l - m, t - m, r + m, b + m)
    }

    companion object {
        const val INK = 0L
    }
}

/** Un dessin : une page de [WIDTH] de large et [height] de haut, et ses traits. */
internal class InkDrawing(val height: Float, val strokes: List<InkStroke>) {
    val isEmpty: Boolean get() = strokes.isEmpty()

    fun with(strokes: List<InkStroke>) = InkDrawing(height, strokes)

    fun taller() = InkDrawing(height + PAGE_STEP, strokes)

    companion object {
        const val WIDTH = 1000f
        const val PAGE_STEP = 700f
        val Empty = InkDrawing(PAGE_STEP * 2, emptyList())
    }
}

/**
 * Le dessin en octets, puis en Base64 : il voyage dans le JSON de la note.
 *
 * Format, version 1 : un octet de version, la hauteur, le nombre de traits ; pour
 * chaque trait l'outil, la couleur, l'épaisseur, le nombre de points, puis le premier
 * point en absolu et les suivants en écart avec le précédent, chacun suivi de sa
 * pression sur un octet. Positions au dixième d'unité, en entiers variables : un point
 * prend trois ou quatre octets, un dessin chargé quelques dizaines de kilo-octets.
 */
@OptIn(ExperimentalEncodingApi::class)
internal object InkCodec {
    private const val VERSION = 1

    fun encode(d: InkDrawing): String {
        val out = Bytes()
        out.byte(VERSION)
        out.varint(d.height.toTenths())
        out.varint(d.strokes.size.toLong())
        for (s in d.strokes) {
            out.byte(s.tool.ordinal)
            out.varint(s.color)
            out.varint(s.width.toTenths())
            out.varint(s.points.size.toLong())
            var px = 0L
            var py = 0L
            for (pt in s.points) {
                val x = pt.x.toTenths()
                val y = pt.y.toTenths()
                out.zigzag(x - px)
                out.zigzag(y - py)
                out.byte((pt.p.coerceIn(0f, 1f) * 255).roundToInt())
                px = x
                py = y
            }
        }
        return Base64.encode(out.toByteArray())
    }

    /** Le dessin, ou null si le texte n'en est pas un : une note abîmée ne doit pas planter l'écran. */
    fun decode(text: String): InkDrawing? = runCatching {
        val r = Reader(Base64.decode(text))
        require(r.byte() == VERSION)
        val height = r.varint() / 10f
        val strokes = List(r.varint().toInt()) {
            val tool = InkTool.entries[r.byte()]
            val color = r.varint()
            val width = r.varint() / 10f
            var x = 0L
            var y = 0L
            val points = List(r.varint().toInt()) {
                x += r.zigzag()
                y += r.zigzag()
                InkPoint(x / 10f, y / 10f, r.byte() / 255f)
            }
            InkStroke(tool, color, width, points)
        }
        InkDrawing(height, strokes)
    }.getOrNull()

    private fun Float.toTenths(): Long = (this * 10).roundToInt().toLong()

    private class Bytes {
        private var buf = ByteArray(256)
        private var n = 0
        fun byte(v: Int) {
            if (n == buf.size) buf = buf.copyOf(buf.size * 2)
            buf[n++] = v.toByte()
        }
        fun varint(value: Long) {
            var v = value
            while (v and 0x7FL.inv() != 0L) { byte(((v and 0x7F) or 0x80).toInt()); v = v ushr 7 }
            byte(v.toInt())
        }
        fun zigzag(v: Long) = varint((v shl 1) xor (v shr 63))
        fun toByteArray() = buf.copyOf(n)
    }

    private class Reader(private val buf: ByteArray) {
        private var i = 0
        fun byte(): Int = buf[i++].toInt() and 0xFF
        fun varint(): Long {
            var shift = 0
            var result = 0L
            while (true) {
                val b = byte()
                result = result or ((b and 0x7F).toLong() shl shift)
                if (b and 0x80 == 0) return result
                shift += 7
            }
        }
        fun zigzag(): Long = varint().let { (it ushr 1) xor -(it and 1) }
    }
}

/**
 * Les pièces jointes d'une note, telles que rangées dans sa colonne `attachments` : un
 * tableau JSON d'objets `{"id", "type", …}`. Un dessin y est `{"id", "type": "ink",
 * "data"}`. Ce qu'on ne connaît pas (une image, plus tard) est gardé tel quel.
 */
internal object NoteAttachments {
    private val json = Json { ignoreUnknownKeys = true }

    /** Taille au-delà de laquelle une note refuse un trait de plus : la synchro l'enverrait mal. */
    const val MAX_INK_CHARS = 600_000

    private fun parse(raw: String?): List<JsonObject> =
        raw?.takeIf { it.isNotBlank() }
            ?.let { runCatching { json.parseToJsonElement(it) as? JsonArray }.getOrNull() }
            ?.mapNotNull { it as? JsonObject }
            .orEmpty()

    private fun JsonObject.str(key: String) = this[key]?.jsonPrimitive?.contentOrNull

    /** Les dessins de la note, par identifiant, encore encodés. */
    fun inks(raw: String?): Map<String, String> =
        parse(raw).filter { it.str("type") == "ink" }
            .mapNotNull { o -> o.str("id")?.let { id -> o.str("data")?.let { id to it } } }
            .toMap()

    /** Le poids des dessins d'une note, en caractères. */
    fun inkSize(raw: String?): Int = inks(raw).values.sumOf { it.length }

    /** Pose ou remplace le dessin [id] ; null pour l'ôter. Rend la colonne, ou null si elle est vide. */
    fun withInk(raw: String?, id: String, data: String?): String? {
        val others = parse(raw).filterNot { it.str("id") == id }
        val all = if (data == null) others else others + buildJsonObject {
            put("id", id)
            put("type", "ink")
            put("data", data)
        }
        return if (all.isEmpty()) null else JsonArray(all).toString()
    }

    /** Les dessins qui ne sont plus cités dans [body] : le texte qui les citait a été effacé. */
    fun orphanInks(raw: String?, body: String): Set<String> =
        inks(raw).keys.filterTo(mutableSetOf()) { id -> "(ink:$id)" !in body }
}
